import getTableColumns, { getDefaultSettings } from './helpers/tableColumns';
import useFlexibleColumnsTable from '../../../hooks/useFlexibleColumnsTable';
import { LOCAL_STORAGE_KEYS } from '../../../shared/settingsNames';
import { getCellReducers } from './helpers/getCellReducers';
import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import { useAccess } from '../../../hooks/useAccess';
import {
  useDeleteAdminUser,
  useGetAdminUsers,
} from '../../../services/redux/api/usersApi';
import { IUser } from '../../../types/interfaces/user';
import { FormikProps, useFormik } from 'formik';

export function useAdminUsersComponent(classes) {
  const { t } = useTranslation();
  const { data: adminUsers } = useGetAdminUsers();
  const [itemForDelete, setItemForDelete] = useState<IUser>(null);
  const [itemForEditing, setItemForEditing] = useState<IUser>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>('');
  const { access } = useAccess();

  const [deleteAdminUser] = useDeleteAdminUser();
  const { order: defaultOrder, orderBy } = getDefaultSettings(t);
  const allOption = { id: 'all', name: t('common.all') };
  const filterFormik: FormikProps<any> = useFormik({
    enableReinitialize: true,
    initialValues: {
      role: allOption,
      active: allOption,
      hidden: allOption,
    },
    onSubmit: () => {},
  });
  const filteredValues = useMemo(
    () =>
      adminUsers?.filter((user: IUser) => {
        const roleFilterId = filterFormik.values.role?.id ?? 'all';
        const activeFilterId = filterFormik.values.active?.id ?? 'all';
        const hiddenFilterId = filterFormik.values.hidden?.id ?? 'all';
        const filterValue = filter.toLowerCase();
        const includesFilter =
          String(user.firstname ?? '')
            .toLowerCase()
            .includes(filterValue) ||
          String(user.lastname ?? '')
            .toLowerCase()
            .includes(filterValue) ||
          String(user.email ?? '')
            .toLowerCase()
            .includes(filterValue) ||
          String(user.role?.name ?? '')
            .toLowerCase()
            .includes(filterValue) ||
          String(user.uid ?? '')
            .toLowerCase()
            .includes(filterValue);

        if (!includesFilter) return false;

        if (roleFilterId !== 'all' && user.role?.id !== roleFilterId)
          return false;

        if (activeFilterId !== 'all') {
          const isActive = !!user.active;
          if (
            (activeFilterId === true && !isActive) ||
            (activeFilterId === false && isActive)
          ) {
            return false;
          }
        }

        if (hiddenFilterId !== 'all') {
          const isHidden = !!user.hidden;
          if (
            (hiddenFilterId === true && !isHidden) ||
            (hiddenFilterId === false && isHidden)
          ) {
            return false;
          }
        }

        return true;
      }),
    [adminUsers, filter, filterFormik.values],
  );

  const cellReducers = getCellReducers(
    t,
    classes,
    {
      canEdit: access?.adminUsers?.canEdit ?? false,
      canDelete: access?.adminUsers?.canDelete ?? false,
    },
    (restriction: IUser) => {
      setItemForEditing(restriction);
      setShowEditModal(true);
    },
    (restriction: IUser) => {
      setItemForDelete(restriction);
    },
  );

  const useTableData = useFlexibleColumnsTable(
    filteredValues,
    LOCAL_STORAGE_KEYS.ADMIN_USER_TABLE_SETTINGS,
    getTableColumns(t),
    orderBy,
    defaultOrder,
  );

  const {
    order,
    columnsListForPopup,
    columnsList,
    onToggleHideColumn,
    onRestoreDefault,
    sortedArray,
    onSortColumns,
    onSort,
  } = useTableData;

  function onItemDelete() {
    setItemForDelete(null);
    deleteAdminUser({ id: itemForDelete.id });
  }

  const filterRoleOptions = [
    allOption,
    ...Array.from(new Set(adminUsers?.map((user) => user.role?.id) || []))
      .filter((id): id is string => !!id)
      .map((id) => ({
        id,
        name: adminUsers.find((user) => user.role?.id === id)?.role?.name || id,
      })),
  ];

  const filterActiveOptions = [
    allOption,
    { id: true, name: t('common.active') },
    { id: false, name: t('common.notActive') },
  ];

  const filterHiddenOptions = [
    allOption,
    { id: true, name: t('common.yes') },
    { id: false, name: t('common.no') },
  ];

  return {
    sortedArray,
    columnsList,
    order,
    orderBy,
    onSort,
    onSortColumns,
    cellReducers,
    itemForDelete,
    setItemForDelete,
    onItemDelete,
    filter,
    setFilter,
    showEditModal,
    setShowEditModal,
    itemForEditing,
    setItemForEditing,
    filterFormik,
    filterRoleOptions,
    filterActiveOptions,
    filterHiddenOptions,
  };
}
